import React from "react";
import styled from "styled-components";
import { router, Link } from "@inertiajs/react";
import { UtensilsCrossed } from "lucide-react";
import { useLanguage } from "../../../../contexts/language";
import { PageHeader } from "../../../../components/page-header";
import { getOrderHistoryFromLocal } from "../../../../util";

const Container = styled.div`
  min-height: 100vh;
  background: #f9f9f9;
`;

const Wrapper = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 16px 20px;
`;

const Card = styled.button`
  width: calc(100% - 24px);
  border: 1px solid #e5e5e5;
  background: #fff;
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 12px;
  text-align: left;
  cursor: pointer;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const OrderNumber = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #222;
`;

const OrderTotal = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #c9a961;
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 6px;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 500;
  color: ${(props) => props.$color || "#666"};
`;

const StatusDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${(props) => props.$color || "#999"};
`;

const OrderDate = styled.div`
  font-size: 12px;
  color: #999;
  margin-top: 8px;
`;

const Empty = styled.div`
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  padding: 16px;
  color: #777;
`;

const OrderMoreLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: calc(100% - 24px);
  padding: 12px 16px;
  margin-bottom: 16px;
  border: 1px dashed #c9a961;
  border-radius: 12px;
  background: rgba(201, 169, 97, 0.04);
  color: #c9a961;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(201, 169, 97, 0.1);
    box-shadow: 0 2px 8px rgba(201, 169, 97, 0.15);
  }
`;

const getStatusColor = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "paid" || s === "finished") return "#0f7a39";
  if (s === "pending" || s === "pending_payment") return "#d97706";
  if (s === "on_progress") return "#155d9b";
  if (s === "expired" || s === "failed" || s === "canceled") return "#a1152d";
  return "#999";
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch (_e) {
    return "";
  }
};

export default function OrdersIndexPage(props) {
  const { building_code } = props;
  const { t } = useLanguage();
  const [orders, setOrders] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    const load = async () => {
      const history = getOrderHistoryFromLocal(building_code);
      if (!history.length) {
        setOrders([]);
        setIsLoading(false);
        return;
      }

      const fetched = await Promise.all(
        history.map(async (entry) => {
          try {
            const response = await fetch(`/api/v1/orders/${entry.id}`, {
              headers: { Accept: "application/json" },
            });
            if (!response.ok) return null;
            const payload = await response.json();
            return payload?.data || null;
          } catch (_e) {
            return null;
          }
        }),
      );

      setOrders(fetched.filter(Boolean));
      setIsLoading(false);
    };

    load();
  }, [building_code]);

  return (
    <Container>
      <PageHeader
        title={t.orders.title}
        backPath={`/hebe/${building_code}/hgs/`}
        buildingCode={building_code}
      />
      <Wrapper>
        <OrderMoreLink href={`/hebe/${building_code}/hgs/fnb`}>
          <UtensilsCrossed size={16} />
          {t.orders.orderMore}
        </OrderMoreLink>

        {isLoading ? (
          <Empty>{t.order.loadingOrders}</Empty>
        ) : orders.length === 0 ? (
          <Empty>{t.order.noOrdersFound}</Empty>
        ) : (
          orders.map((order) => (
            <Card
              key={order.id}
              onClick={() => router.visit(`/hebe/${building_code}/hgs/orders/${order.id}`)}
            >
              <CardTop>
                <OrderNumber>{order.order_number || order.id}</OrderNumber>
                <OrderTotal>{order.total || "-"}</OrderTotal>
              </CardTop>
              <StatusRow>
                <StatusBadge $color={getStatusColor(order.payment_status)}>
                  <StatusDot $color={getStatusColor(order.payment_status)} />
                  {order.payment_status || "-"}
                </StatusBadge>
                <StatusBadge $color={getStatusColor(order.delivery_status)}>
                  <StatusDot $color={getStatusColor(order.delivery_status)} />
                  {(order.delivery_status || "-").replace("_", " ")}
                </StatusBadge>
              </StatusRow>
              {order.created_at && (
                <OrderDate>{formatDate(order.created_at)}</OrderDate>
              )}
            </Card>
          ))
        )}
      </Wrapper>
    </Container>
  );
}
